/**
 * Content Types API endpoints.
 *
 * Manage custom content types and their fields.
 * All endpoints are site-scoped.
 */

import { api } from './client';
import type {
  ContentType,
  ContentTypeResult,
  ContentTypeWithFields,
  ContentField,
  SiteSchema,
  ApiResponse,
  CreateContentTypeRequest,
  UpdateContentTypeRequest,
  AddFieldRequest,
  UpdateFieldRequest,
  ReorderFieldsRequest,
} from '../types';

/** Content Types API */
export const contentTypesApi = {
  /** List all content types for a site */
  list: (siteSlug: string) =>
    api.get<ApiResponse<ContentType[]>>(`/v1/sites/${siteSlug}/content-types`),

  /** Get a content type with its fields */
  get: (siteSlug: string, typeSlug: string) =>
    api.get<ApiResponse<ContentTypeWithFields>>(`/v1/sites/${siteSlug}/content-types/${typeSlug}`),

  /** Create a new content type */
  create: (siteSlug: string, data: CreateContentTypeRequest) =>
    api.post<ApiResponse<ContentTypeResult>>(`/v1/sites/${siteSlug}/content-types`, data),

  /** Update a content type */
  update: (siteSlug: string, typeSlug: string, data: UpdateContentTypeRequest) =>
    api.put<ApiResponse<ContentTypeResult>>(`/v1/sites/${siteSlug}/content-types/${typeSlug}`, data),

  /** Delete a content type */
  delete: (siteSlug: string, typeSlug: string, force = false) =>
    api.delete<ApiResponse<null>>(`/v1/sites/${siteSlug}/content-types/${typeSlug}${force ? '?force=true' : ''}`),
};

/** Content Fields API */
export const contentFieldsApi = {
  /** List fields for a content type */
  list: (siteSlug: string, typeSlug: string) =>
    api.get<ApiResponse<ContentField[]>>(`/v1/sites/${siteSlug}/content-types/${typeSlug}/fields`),

  /** Add a field to a content type */
  add: (siteSlug: string, typeSlug: string, data: AddFieldRequest) =>
    api.post<ApiResponse<ContentField>>(`/v1/sites/${siteSlug}/content-types/${typeSlug}/fields`, data),

  /** Update a field */
  update: (siteSlug: string, typeSlug: string, fieldSlug: string, data: UpdateFieldRequest) =>
    api.put<ApiResponse<ContentField>>(`/v1/sites/${siteSlug}/content-types/${typeSlug}/fields/${fieldSlug}`, data),

  /** Delete a field */
  delete: (siteSlug: string, typeSlug: string, fieldSlug: string) =>
    api.delete<ApiResponse<null>>(`/v1/sites/${siteSlug}/content-types/${typeSlug}/fields/${fieldSlug}`),

  /** Reorder fields */
  reorder: (siteSlug: string, typeSlug: string, data: ReorderFieldsRequest) =>
    api.put<ApiResponse<null>>(`/v1/sites/${siteSlug}/content-types/${typeSlug}/fields/reorder`, data),
};

/** Site Schema API */
export const schemaApi = {
  /** Get full site schema (all content types with fields and relations) */
  get: (siteSlug: string) =>
    api.get<ApiResponse<SiteSchema>>(`/v1/sites/${siteSlug}/schema`),
};
